import {
  classes,
  items,
  mobs,
  npcs,
  pvp,
  quests,
  skills,
  store,
  structures,
  wheel,
  zones,
} from "@two-crowns/data";
import trJson from "./data/tr.json";
import { keepEnglish, lang } from "./index";

/** Turkish names and texts of game data, by id. Anything missing stays English. */
interface DataTable {
  items?: Record<string, string>;
  mobs?: Record<string, string>;
  skills?: Record<string, string>;
  classes?: Record<string, { name?: string }>;
  zones?: Record<string, string>;
  npcs?: Record<string, { name?: string; title?: string; greeting?: string }>;
  /** KO's townsfolk by their English text (titles, greetings, names that are titles). */
  koNpcText?: Record<string, string>;
  quests?: Record<string, { name?: string; text?: string }>;
  store?: Record<string, string>;
  structures?: Record<string, string>;
  /** PvP rank names (pvp.json ranks) and Riftstone Reel prize labels (wheel.json), by English. */
  pvpRanks?: Record<string, string>;
  wheelLabels?: Record<string, string>;
}

let done = false;
/** English data name to its localized name (for server messages written in English). */
const NAMES = new Map<string, string>();

/** A game data name (item, monster, zone, skill) in the chosen language, as the server wrote it. */
export function localName(en: string): string {
  return NAMES.get(en) ?? en;
}

/**
 * Renames the game data in this page's copy for the chosen language, once, before anything
 * reads it. Only what players read changes (names, titles, texts); ids stay the same, and the
 * server keeps its own English copy.
 */
export function localizeData() {
  if (done || lang() !== "tr") return;
  done = true;
  const tr = trJson as DataTable;
  const put = <T extends { id: string; name: string }>(
    list: readonly T[],
    names?: Record<string, string>,
  ) => {
    if (!names) return;
    for (const o of list) {
      const n = names[o.id];
      if (!n) continue;
      NAMES.set(o.name, n);
      (o as { name: string }).name = n;
    }
  };
  // KO's items keep their English names (owner 2026-10-26)
  const ko = items.filter((i) => i.id.startsWith("ko_"));
  keepEnglish(ko.map((i) => i.name));
  put(
    items.filter((i) => !i.id.startsWith("ko_")),
    tr.items,
  );
  put(mobs, tr.mobs);
  put(skills, tr.skills);
  put(zones, tr.zones);
  for (const c of classes) {
    const n = tr.classes?.[c.id]?.name;
    if (n) (c as { name: string }).name = n;
  }
  for (const n of npcs) {
    // KO's townsfolk (local experiment): their titles, greetings and title-names by their text
    const ko = tr.koNpcText;
    if (ko && n.id.startsWith("ko_")) {
      const o = n as { name: string; title: string; greeting: string; about?: string };
      o.name = ko[o.name] ?? o.name;
      o.title = ko[o.title] ?? o.title;
      o.greeting = ko[o.greeting] ?? o.greeting;
      if (o.about) o.about = ko[o.about] ?? o.about;
    }
    const x = tr.npcs?.[n.id];
    if (!x) continue;
    const o = n as { name: string; title: string; greeting: string };
    if (x.name) o.name = x.name;
    if (x.title) o.title = x.title;
    if (x.greeting) o.greeting = x.greeting;
  }
  for (const q of quests) {
    const x = tr.quests?.[q.id];
    if (!x) continue;
    const o = q as { name: string; text: string };
    if (x.name) {
      NAMES.set(o.name, x.name);
      o.name = x.name;
    }
    if (x.text) o.text = x.text;
  }
  for (const list of [
    store.wings,
    store.cloaks,
    store.skins,
    store.glows,
    store.items,
    store.premium,
    store.gemPacks,
  ])
    put(list as unknown as { id: string; name: string }[], tr.store);
  for (const r of pvp.ranks) {
    const n = tr.pvpRanks?.[r.name];
    if (n) (r as { name: string }).name = n;
  }
  // The monthly place symbols share the rank names' table.
  for (const r of pvp.placeSymbols) {
    const n = tr.pvpRanks?.[r.name];
    if (n) (r as { name: string }).name = n;
  }
  for (const tier of Object.values(wheel.tiers))
    for (const seg of tier.segments) {
      const n = seg.label && tr.wheelLabels?.[seg.label];
      if (n) (seg as { label?: string }).label = n;
    }
  const structs = structures as unknown as { id: string; name?: string }[];
  for (const s of structs) {
    const n = tr.structures?.[s.id];
    if (n) s.name = n;
  }
}
